import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard,
  DollarSign,
  Globe,
  Bitcoin,
  Mail,
  ChevronDown,
  Check,
} from "lucide-react";

const paymentOptions = [
  { value: "Paypal", icon: <Mail className="text-[#003087]" />, color: "bg-[#003087]/10" },
  { value: "Payoneer", icon: <Globe className="text-[#FF4800]" />, color: "bg-[#FF4800]/10" },
  { value: "BTC", icon: <Bitcoin className="text-[#F7931A]" />, color: "bg-[#F7931A]/10" },
  { value: "Bank/Wire Transfer", icon: <CreditCard className="text-[#6A0DAD]" />, color: "bg-[#6A0DAD]/10" },
  { value: "UPI", icon: <DollarSign className="text-[#0A9D58]" />, color: "bg-[#0A9D58]/10" },
];

export default function PaymentMethodForm({ onSubmit, onCancel, initialData }) {
  const [form, setForm] = useState({
    method: "",
    details: "",
    bankName: "",
    accountName: "",
    accountNumber: "",
    swiftCode: "",
    iban: "",
    bankAddress: "",
    postalCode: "",
    achRoutingNo: "",
    fedwireNo: "",
    city: "",
    country: "",
    branchCode: "",
    paypalEmail: "",
    payoneerEmail: "",
    btcAddress: "",
    upiId: "",
  });


  const [errors, setErrors] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({ ...form, ...initialData });
      setSelectedOption(paymentOptions.find(opt => opt.value === initialData.method) || null);
    } else {
      setForm({
        method: "",
        details: "",
        bankName: "",
        accountName: "",
        accountNumber: "",
        swiftCode: "",
        iban: "",
        bankAddress: "",
        postalCode: "",
        achRoutingNo: "",
        fedwireNo: "",
        city: "",
        country: "",
        branchCode: "",
        paypalEmail: "",
        payoneerEmail: "",
        btcAddress: "",
      });
      setSelectedOption(null);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" }); // clear field error
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setForm({ ...form, method: option.value });
    setErrors({});
    setIsDropdownOpen(false);
  };

  // 🔹 Validation Rules
  const validateForm = () => {
    const newErrors = {};

    if (!form.method) newErrors.method = "Payment method is required.";

    if (form.method === "Bank/Wire Transfer") {
      if (!form.bankName.trim()) newErrors.bankName = "Bank Name is required.";
      if (!form.accountName.trim()) newErrors.accountName = "Account Name is required.";

      // ✅ Account number must be numeric (no letters)
      if (!/^\d+$/.test(form.accountNumber.trim())) {
        newErrors.accountNumber = "Account Number must contain only digits.";
      }

      // ✅ IBAN must start with letters and be alphanumeric
      if (form.iban && !/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/i.test(form.iban)) {
        newErrors.iban = "Invalid IBAN format.";
      }

      // ✅ SWIFT code format: 8 or 11 characters (letters/numbers)
      if (form.swiftCode && !/^[A-Z0-9]{8,11}$/i.test(form.swiftCode)) {
        newErrors.swiftCode = "Invalid SWIFT Code format.";
      }

      // ✅ Postal Code: alphanumeric check
      if (form.postalCode && !/^[A-Za-z0-9\s-]{3,10}$/.test(form.postalCode)) {
        newErrors.postalCode = "Invalid postal code.";
      }

      // ✅ ACH and Fedwire optional but format check
      if (form.achRoutingNo && !/^\d{9}$/.test(form.achRoutingNo)) {
        newErrors.achRoutingNo = "ACH Routing No must be 9 digits.";
      }

      if (form.fedwireNo && !/^\d{9}$/.test(form.fedwireNo)) {
        newErrors.fedwireNo = "Fedwire No must be 9 digits.";
      }
    }

    if (form.method === "Paypal" || form.method === "Payoneer") {
      if (!form.details.trim()) newErrors.details = "Email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.details))
        newErrors.details = "Invalid email format.";
    }

    if (form.method === "BTC") {
      if (!form.details.trim()) newErrors.details = "BTC address is required.";
      else if (!/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(form.details))
        newErrors.details = "Invalid Bitcoin address format.";
    }

    if (form.method === "UPI") {
      if (!form.details.trim()) {
        newErrors.details = "UPI ID is required.";
      } else if (!/^[\w.-]{2,256}@[a-zA-Z]{2,64}$/.test(form.details)) {
        newErrors.details = "Invalid UPI ID format.";
      }
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit(form);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 w-full max-w-md"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {initialData ? "Edit Payment" : "Add Payment Method"}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-3">
              {paymentOptions.map((option) => (
                <motion.button
                  key={option.value}
                  type="button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleOptionSelect(option)}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${selectedOption?.value === option.value
                      ? `border-blue-500 ${option.color} ring-2 ring-blue-500/30`
                      : "border-gray-200 dark:border-gray-700 hover:border-blue-300"
                    }`}
                >
                  <span className="text-2xl mb-1">{option.icon}</span>
                  <span className="text-xs font-medium">{option.value}</span>
                </motion.button>
              ))}
            </div>
            {errors.method && (
              <p className="text-red-500 text-xs mt-1">{errors.method}</p>
            )}
          </div>

          {/* Dynamic Fields */}
          {selectedOption?.value === "Bank/Wire Transfer" && (
            <div className="space-y-3">
              {[
                "bankName",
                "accountName",
                "accountNumber",
                "swiftCode",
                "iban",
                "bankAddress",
                "postalCode",
                "city",
                "country",
                "branchCode",
                "achRoutingNo",
                "fedwireNo",
              ].map((field) => (
                <div key={field}>
                  <input
                    type="text"
                    name={field}
                    value={form[field] || ""}
                    onChange={handleChange}
                    placeholder={field.replace(/([A-Z])/g, " $1")}
                    className={`w-full px-3 py-2 rounded-lg border ${errors[field]
                        ? "border-red-500"
                        : "border-gray-200 dark:border-gray-700"
                      } bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
                  />
                  {errors[field] && (
                    <p className="text-red-500 text-xs mt-1">{errors[field]}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {(selectedOption?.value === "Paypal" ||
            selectedOption?.value === "Payoneer") && (
              <div>
                <input
                  type="email"
                  name="details"
                  value={form.details}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={`w-full px-4 py-2 rounded-lg border ${errors.details
                      ? "border-red-500"
                      : "border-gray-200 dark:border-gray-700"
                    } bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500`}
                />
                {errors.details && (
                  <p className="text-red-500 text-xs mt-1">{errors.details}</p>
                )}
              </div>
            )}

          {selectedOption?.value === "BTC" && (
            <div>
              <input
                type="text"
                name="details"
                value={form.details}
                onChange={handleChange}
                placeholder="Bitcoin Address"
                className={`w-full px-4 py-2 rounded-lg border ${errors.details
                    ? "border-red-500"
                    : "border-gray-200 dark:border-gray-700"
                  } bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500`}
              />
              {errors.details && (
                <p className="text-red-500 text-xs mt-1">{errors.details}</p>
              )}
            </div>
          )}

          {selectedOption?.value === "UPI" && (
            <div>
              <input
                type="text"
                name="details"
                value={form.details}
                onChange={handleChange}
                placeholder="yourname@upi / mobile@upi"
                className={`w-full px-4 py-2 rounded-lg border ${errors.details
                    ? "border-red-500"
                    : "border-gray-200 dark:border-gray-700"
                  } bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500`}
              />
              {errors.details && (
                <p className="text-red-500 text-xs mt-1">{errors.details}</p>
              )}
            </div>
          )}


          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedOption}
              className={`px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium shadow-sm hover:shadow-md transition-all ${!selectedOption ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              {initialData ? "Update Method" : "Add Method"}
            </button>
          </div>
        </form>
      </motion.div>
    </AnimatePresence>
  );
}
